import React, { useContext, useEffect, useMemo, useState } from 'react'
import { ApolloError } from '@apollo/client'
import { useMutation } from '@apollo/client'
import classnames from 'classnames'
import { Paragraph, Spinner } from '@scentregroup/ui'
import AnalyticsDataWrapper from '@scentregroup/shared/components/analytics-data-wrapper'
import { displayStringForPriceRangeWithoutCurrency } from '@scentregroup/shared/helpers/product-price'
import {
  getProductStockIssue,
  IssueType,
} from '@scentregroup/shared/helpers/stock-level-issue-messages'
import useIsMounted from '@scentregroup/shared/hooks/use-is-mounted'
import PotentialLink from '@scentregroup/shared/hub-components/potential-link'
import { generateStoreFrontUrl } from '@scentregroup/shared/helpers/store'
import { ShippingDeadlineResult } from '../../../../lib/get-shipping-deadlines'
import { INotification } from '../../types/notification'
import {
  IMarketplaceCartItem,
  IAddToCartResponse,
  isAddToCartMutationResult,
} from '../../types/cart'
import { getMaxQuantity } from '../../lib/product'
import { useCookie, CookieName } from '../../lib/cookie'
import { onError } from '../../lib/on-error'
import {
  getInitialOptionValuesFromVariant,
  getPossibleProductVariants,
  getProductVariant,
  getProductVariantBySku,
} from '../../lib/product-form-inputs'
import { AddProductToCart } from '../../graphql/mutations'
import useForm, { IFormChangeEvent } from '../../form/use-form'
import { IMKPProductValues } from '../../types/product-form'
import ProductForm, { OnSubmitFnArgs } from '../../components/product-form'
import ImageGallery from '../../components/image-gallery'
import {
  MarketplaceProduct,
  IssueSeverity,
} from '../../types/marketplace-product'
import AddToCartDialog from './add-to-cart-dialog'
import VisualGuideDialog from '../../components/visual-guide-dialog'
import ItemFulfilmentPanel from '../../components/item-fulfilment-panel'
import Breadcrumbs from '../../components/breadcrumbs'
import { MKPProductValidate } from '../../form/product-validation'
import { useVisualGuideContext } from '../../lib/use-visual-guide-context'
import ProductDetailsAccordion from './product-details-accordion'
import style from './index.module.scss'
import ProductTitle from './product-title'
import ProductPrice from '../../components/product-form/product-price'
import ErrorFeature from '../../components/error-feature'
import HttpStatusCode from '../../../server/lib/http-codes'
import { IAddProductToCartInput } from '../../types/add-to-cart'
import { PRODUCTS, PROMOTION } from '../../paths'
import { createCategoryBreadcrumbs } from '../../lib/breadcrumbs'
import { useRouter } from 'next/router'
import { IProductImage } from '../../types/product'
import Head from 'next/head'
import makeUniqueProductId from '@scentregroup/shared/helpers/make-unique-product-id'
import makeImageUrl from '@scentregroup/shared/hub-components/cloudinary-image/helpers/make-image-url'
import { FeatureFlagsContext } from '@scentregroup/shared/context'
import {
  commonApplicablePromotions,
  discountedPriceRange,
  discountPercentageRange,
  getInitialOptionValues,
  INITIAL_VALUES,
  isProductReturnable,
  priceRange,
  updateUrlWithVariant,
  onProductSubmitted,
  useTrackViewOutOfStockError,
  onSubmit,
  createTrackable,
} from './marketplace-helpers'
import { urlFragmentParams } from '@scentregroup/shared/lib/url-fragment-params'
import { MARKETPLACE_PRODUCTS_INDEX_DEFAULT_AU } from '@scentregroup/shared/constants'
import { useAnalytics } from '@scentregroup/shared/analytics'
import { Link } from '@hub/link'
import { useTrackVariantSelectionAnalytics } from '../../lib/analytics-view-item'
import { makeAnalyticsProductAddedData } from '../../lib/analytics-cart'
import { useWWOOBanner } from '@scentregroup/shared/hooks/use-wwoo-banner'
import { RelatedProductsGroup } from './related-products/related-products-group'
import { useInView } from 'react-intersection-observer'
import IsAboveFoldProvider from '@scentregroup/shared/hub-components/is-above-fold-provider'

export interface IProductPage {
  categoryIdPath?: string[]
  product: MarketplaceProduct
  cartId?: string
  shippingCost: number
  shippingDeadline: ShippingDeadlineResult
  retailerSizeGuide?: string
  defaultSizeGuide?: string | null
  setCartId: (val: string) => void
  onNotification: (notification: INotification) => void
}

const OgImage: React.FunctionComponent<
  React.PropsWithChildren<{
    images?: IProductImage[]
  }>
> = ({ images }) => {
  if (!images || !images[0]?.url) {
    return null
  }

  const URL_PREFIX = 'https://res.cloudinary.com/scentre-group-au/image/fetch/'
  const imageUrl = `${URL_PREFIX}/c_fit,q_80,w_1024/${images[0].url}`

  return (
    <Head>
      <meta key="og:image" property="og:image" content={imageUrl} />
      <meta
        key="twitter:card"
        name="twitter:card"
        content="summary_large_image"
      />
    </Head>
  )
}

const ProductPage: React.FunctionComponent<
  React.PropsWithChildren<IProductPage>
> = ({
  product,
  cartId,
  setCartId,
  onNotification,
  shippingCost,
  shippingDeadline,
  retailerSizeGuide,
  defaultSizeGuide,
}) => {
  useWWOOBanner()
  const isBrowser = useIsMounted()

  const { trackEvent } = useAnalytics()

  const [expandTermsAndConditions, setExpandTermsAndConditions] =
    useState(false)

  const [windowSize, setWindowSize] = useState(() => {
    if (isBrowser) {
      return window.innerWidth
    }
    return 1024
  })

  useEffect((): undefined | (() => void) => {
    if (!isBrowser) {
      return
    }
    const handleWindowResize = (): void => setWindowSize(window.innerWidth)

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [isBrowser])

  const showMobileLayout = windowSize < 928

  const featureFlags = useContext(FeatureFlagsContext)

  const router = useRouter()
  const [, setCartCount] = useCookie(CookieName.CART_COUNT)
  const [addedProduct, setAddedProduct] = useState<
    IMarketplaceCartItem | undefined
  >()

  const { visualGuideInfo, hideVisualGuide } = useVisualGuideContext()

  const [variantSku, setVariantSku] = React.useState(() => {
    return (
      (Array.isArray(router.query.v) ? router.query.v[0] : router.query.v) ?? ''
    )
  })
  React.useEffect(() => {
    setVariantSku(
      (Array.isArray(router.query.v) ? router.query.v[0] : router.query.v) ?? ''
    )
  }, [router.query.v])

  const requestedVariant = getProductVariantBySku(product, variantSku)

  const selectedOptions = requestedVariant
    ? getInitialOptionValuesFromVariant(requestedVariant)
    : null

  const form = useForm<IMKPProductValues, OnSubmitFnArgs>(
    MKPProductValidate(product),
    onSubmit,
    {
      ...INITIAL_VALUES,
      ...getInitialOptionValues(product),
      ...selectedOptions,
    }
  )

  const params = urlFragmentParams()
  const queryID = params.get('aqid') ?? ''
  const algoliaPosition = params.get('aqpos') ?? ''
  const algoliaIndexName =
    params.get('aqin') ?? MARKETPLACE_PRODUCTS_INDEX_DEFAULT_AU

  const possibleProductVariants = getPossibleProductVariants(
    product,
    form.values
  )

  const handleProductOptionsChange = (e: IFormChangeEvent): void => {
    const updatedValues = {
      ...form.values,
      [e.name]: e.value,
    }
    const newVariant = getProductVariant(product, updatedValues, true)
    setVariantSku(newVariant?.sku ?? '')
    updateUrlWithVariant(newVariant?.sku ?? null)
  }

  const selectedVariant = getProductVariant(product, form.values)

  const isReturnable = isProductReturnable(possibleProductVariants)
  const applicablePromotions = commonApplicablePromotions(
    possibleProductVariants
  )

  const productImagesToDisplay = possibleProductVariants[0]?.images.map(
    (obj, index) => ({
      ...obj,
      alt: `${possibleProductVariants[0].title}-${index}`,
    })
  )

  React.useEffect(() => {
    if (selectedVariant?.sku && router.query.v !== selectedVariant.sku) {
      setVariantSku(selectedVariant.sku)
      updateUrlWithVariant(selectedVariant.sku)
    }
  }, [selectedVariant?.sku, router.query.v])

  useTrackVariantSelectionAnalytics(
    product,
    selectedVariant || possibleProductVariants[0] || product.variants[0]
  )

  const [
    addOrUpdateCartItem,
    { loading: addOrUpdateCartItemLoading, error: addToCartError },
  ] = useMutation<IAddToCartResponse, IAddProductToCartInput>(
    AddProductToCart,
    {
      context: { version: 2 },
      onCompleted(data) {
        const quantity = Number(form.values.quantity) ?? 1
        const result = data.addProductToCart

        if (isAddToCartMutationResult(result)) {
          const analyticsEventData = makeAnalyticsProductAddedData(
            product,
            selectedVariant ||
              possibleProductVariants[0] ||
              product.variants[0],
            quantity,
            result.id
          )

          trackEvent({ type: 'legacy', data: analyticsEventData })
        }

        data &&
          onProductSubmitted(
            cartId,
            data,
            setCartId,
            setCartCount,
            setAddedProduct,
            form,
            product,
            selectedVariant
          )
      },
      onError(error: ApolloError) {
        onError(error, onNotification)
      },
    }
  )

  const activeVariants = possibleProductVariants.filter(
    v => !v.issues.find(issue => issue.type === IssueType.OutOfStock)
  )

  // Only showing price range of active variants
  const variantPriceRange = priceRange(activeVariants)
  const variantDiscountedPriceRange = discountedPriceRange(activeVariants)
  const variantDiscountPercentageRange = discountPercentageRange(activeVariants)
  const price = variantDiscountedPriceRange ?? variantPriceRange

  const breadcrumbs = useMemo(() => {
    const category = product.category
    const baseBreadcrumbs = [
      {
        title: 'Home',
        linkTo: '/shop',
      },
    ]
    return createCategoryBreadcrumbs(baseBreadcrumbs, PRODUCTS, category?.path)
  }, [product.category])

  const productTitle = `${product.brand ? `${product.brand} - ` : ''}${
    product.title
  }`

  const productIssue = getProductStockIssue(product.variants, selectedVariant)
  useTrackViewOutOfStockError(productTitle, productIssue)

  const productFormDisabled =
    !requestedVariant ||
    (productIssue !== null && productIssue.severity === IssueSeverity.Error)

  const displayProductPrice = productIssue?.severity !== IssueSeverity.Error

  const logoImageUrl = product.shop.logoUrl
    ? makeImageUrl(
        { type: 'SQUARE', url: product.shop.logoUrl },
        1,
        'crop-center',
        48
      )
    : undefined

  const variantGroupCode = router.query.productId as string

  const { NEXT_PUBLIC_FEAT_FLAG_CART_PREVIEW: isCartPreviewEnabled } =
    useContext(FeatureFlagsContext)

  useEffect(() => {
    if (isCartPreviewEnabled && !addOrUpdateCartItemLoading && addedProduct) {
      window.dispatchEvent(new Event('sg-custom-event-open-cart-preview'))
    }
  }, [isCartPreviewEnabled, addedProduct, addOrUpdateCartItemLoading])
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
  })

  if (addToCartError)
    return <ErrorFeature statusCode={HttpStatusCode.INTERNAL_SERVER_ERROR} />

  return (
    <>
      <OgImage images={productImagesToDisplay} />
      {addOrUpdateCartItemLoading ? (
        // TODO: Move this overlay wrapper as an option to the Spinner in UI
        // or use Modal with new option without a close button
        <div className={style.spinnerOverlay}>
          <Spinner type="large" isSeasonal={false} />
        </div>
      ) : (
        addedProduct &&
        !isCartPreviewEnabled && (
          <AddToCartDialog
            addedProduct={addedProduct}
            setAddedProduct={setAddedProduct}
          />
        )
      )}
      {visualGuideInfo && (
        <VisualGuideDialog
          isOpen={true}
          info={visualGuideInfo}
          retailerSizeGuide={retailerSizeGuide}
          defaultSizeGuide={defaultSizeGuide}
          onDismiss={hideVisualGuide}
        />
      )}
      {product.category && (
        <div className={style.breadCrumbs}>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </div>
      )}
      <div className={classnames(style.productPage)}>
        <div className={classnames(style.columnOne)}>
          {!showMobileLayout && (
            <>
              <IsAboveFoldProvider foldStatus="above">
                <ImageGallery images={productImagesToDisplay} />
              </IsAboveFoldProvider>
              <div
                className={style.desktopAccordions}
                id="promotion-terms-and-conditions"
              >
                <ProductDetailsAccordion
                  product={product}
                  selectedVariant={selectedVariant}
                  returnable={isReturnable}
                  expandTermsAndConditions={expandTermsAndConditions}
                  applicablePromotions={applicablePromotions}
                />
              </div>
            </>
          )}
        </div>
        <div className={classnames(style.columnTwo)}>
          <AnalyticsDataWrapper
            {...createTrackable(
              product,
              selectedVariant,
              variantGroupCode,
              price
            )}
          >
            <div className={style.infoDesktop}>
              <div className={style.shopWrapper}>
                {logoImageUrl && (
                  <img
                    src={logoImageUrl}
                    width={24}
                    className={style.shopLogo}
                    alt={`${product.shop.name} logo`}
                  />
                )}
                Sold by&nbsp;
                <PotentialLink
                  className={style.shop}
                  href={generateStoreFrontUrl(
                    product.shop.retailerId,
                    product.shop.retailerSlug
                  )}
                >
                  {product.shop.name}
                </PotentialLink>
              </div>
              <ProductTitle>{productTitle}</ProductTitle>
              {displayProductPrice && variantPriceRange && (
                <ProductPrice
                  priceRange={variantPriceRange}
                  discountedPriceRange={variantDiscountedPriceRange}
                  discountPercentageRange={variantDiscountPercentageRange}
                  returnable={isReturnable}
                />
              )}
              {applicablePromotions?.[0]?.directPromotion && (
                <div>
                  <div className={style.applicablePromotion}>
                    {/* Temp feature flag till Direct promotion API gets ready */}
                    {featureFlags.NEXT_PUBLIC_FEAT_FLAG_DIRECT_PROMOTION_API_IS_NOT_READY ? (
                      applicablePromotions[0].title
                    ) : (
                      <Link
                        hasUnderline
                        href={`${PROMOTION}/${encodeURIComponent(
                          applicablePromotions[0].directPromotion.sys.id
                        )}/${encodeURIComponent(
                          applicablePromotions[0].directPromotion.slug
                        )}`}
                      >
                        {applicablePromotions[0].title}
                      </Link>
                    )}
                  </div>
                  {applicablePromotions?.some(
                    promo => promo.directPromotion !== null
                  ) && (
                    <Paragraph type="small" className={style.tncLine}>
                      See{' '}
                      <Link
                        hasUnderline
                        href="#promotion-terms-and-conditions"
                        className={style.termsAndConditionsLink}
                        onClick={() => {
                          setExpandTermsAndConditions(true)
                          setTimeout(() => {
                            setExpandTermsAndConditions(false)
                          }, 0)
                        }}
                      >
                        Terms and Conditions
                      </Link>{' '}
                      for details
                    </Paragraph>
                  )}
                </div>
              )}
            </div>
          </AnalyticsDataWrapper>
          {showMobileLayout && (
            <IsAboveFoldProvider foldStatus="above">
              <ImageGallery images={productImagesToDisplay} />
            </IsAboveFoldProvider>
          )}
          <ProductForm
            form={form}
            handleBlur={handleProductOptionsChange}
            product={product as MarketplaceProduct}
            mutation={addOrUpdateCartItem}
            analyticsData={{
              id: product.id,
              uniqueProductId: makeUniqueProductId(
                product.id,
                product.shop.id,
                selectedVariant?.sku
              ),
              variantSku: selectedVariant?.sku,
              title: product.title,
              shop: product.shop,
              variants: selectedVariant?.optionValues
                .map(o => o.value)
                .join('|'),
              price: displayStringForPriceRangeWithoutCurrency(price),
              brand: product.brand,
              category:
                product.category &&
                [...product.category.path].map(cpe => cpe.title).join('/'),
              algoliaObjectID: selectedVariant?.objectID,
              algoliaQueryID: queryID,
              algoliaPosition,
              algoliaIndexName,
              variantGroupCode: variantGroupCode,
              applicablePromotions: selectedVariant?.promotions,
            }}
            maxQuantity={getMaxQuantity(selectedVariant)}
            stockError={productIssue}
            disabled={productFormDisabled}
          />

          {showMobileLayout && (
            <div
              className={style.mobileAccordions}
              id="promotion-terms-and-conditions"
            >
              <ProductDetailsAccordion
                product={product}
                selectedVariant={selectedVariant}
                returnable={isReturnable}
                expandTermsAndConditions={expandTermsAndConditions}
                applicablePromotions={applicablePromotions}
              />
            </div>
          )}
          <ItemFulfilmentPanel
            shippingCost={shippingCost}
            shippingDeadline={shippingDeadline}
          />
        </div>
      </div>
      <div ref={ref}>
        {featureFlags.NEXT_PUBLIC_FEAT_FLAG_RELATED_PRODUCTS && inView && (
          <RelatedProductsGroup
            product={product}
            variantGroupCode={variantGroupCode}
          />
        )}
      </div>
    </>
  )
}

export default ProductPage
